import React, { useEffect, useRef, useState, useCallback } from "react";
import jsQR from "jsqr";
import InstallButton from "../components/InstallButton";
import { api, caisseApi } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, LogOut, Camera, CameraOff } from "lucide-react";

const GAME_LABELS = { roue: "Roue", grattage: "Grattage", memory: "Memory", runner: "Manicou Runner", manuel: "Bon manuel", bo: "Bon manuel" };
const TOKEN_KEY = "yum_caisse_token";
const fmtD = (s) => { try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return "—"; } };
const BON_ST = {
  valid: { txt: "Valide", cls: "bg-[#12301d] text-[#3ecf6f]" },
  redeemed: { txt: "Utilisé ✓", cls: "bg-[#2a2e35] text-[#8b94a0]" },
  expired: { txt: "Expiré", cls: "bg-[#3a1414] text-[#ff8a8a]" },
  disabled: { txt: "Désactivé", cls: "bg-[#3a1414] text-[#ff8a8a]" },
};
const extractCode = (raw) => {
  const s = (raw || "").trim();
  const m = s.match(/([A-Z]{3}-[A-Z0-9]{4,6})\/?$/i) || s.match(/\/v\/([^/?#]+)/i);
  return (m ? m[1] : s).toUpperCase();
};

export default function CaissePage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [pin, setPin] = useState("");
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [manual, setManual] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanOn, setScanOn] = useState(false);
  const [camError, setCamError] = useState("");
  const [mode, setMode] = useState("scan"); // scan | client
  const [clientPhone, setClientPhone] = useState("");
  const [clientData, setClientData] = useState(null);
  const [clientBusy, setClientBusy] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const client = caisseApi(token);

  const logout = () => {
    stopScan();
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setAuthed(false); setVoucher(null);
  };

  const lookup = useCallback(async (code, auto = false) => {
    if (!code) return;
    setBusy(true);
    try {
      const tok = localStorage.getItem(TOKEN_KEY);
      const { data } = await caisseApi(tok).get(`/caisse/voucher/${encodeURIComponent(code)}`);
      // Au SCAN : on valide DIRECTEMENT le bon s'il est utilisable
      if (auto && data.valid && data.status === "valid" && !data.blocked_today) {
        try {
          await caisseApi(tok).post(`/caisse/redeem/${encodeURIComponent(data.code)}`);
          setVoucher({ ...data, redeemed: true, valid: false, status: "redeemed", justRedeemed: true });
        } catch (e2) {
          if (e2?.response?.status === 401) { logout(); return; }
          setVoucher({ ...data, valid: false, error2: e2?.response?.data?.detail || "Validation impossible" });
        }
      } else {
        setVoucher(data);
      }
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      setVoucher({ error: e?.response?.data?.detail || "Bon introuvable" });
    } finally {
      setBusy(false);
    }
  }, []);

  const login = async () => {
    try {
      const { data } = await api.post("/caisse/login", { pin });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      setPin("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PIN incorrect");
    }
  };

  const searchClient = async () => {
    const digits = clientPhone.replace(/\D/g, "");
    if (digits.length < 6) { toast.error("Numéro trop court"); return; }
    setClientBusy(true); setClientData(null);
    try {
      const { data } = await caisseApi(localStorage.getItem(TOKEN_KEY)).get(`/caisse/client?phone=${encodeURIComponent(clientPhone.trim())}`);
      setClientData(data);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); return; }
      setClientData({ error: e?.response?.data?.detail || "Client introuvable" });
    } finally { setClientBusy(false); }
  };

  const redeem = async () => {
    setBusy(true);
    try {
      await client.post(`/caisse/redeem/${encodeURIComponent(voucher.code)}`);
      setVoucher({ ...voucher, redeemed: true, valid: false, status: "redeemed", justRedeemed: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation impossible");
    } finally {
      setBusy(false);
    }
  };

  const stopScan = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanOn(false);
  };

  const startScan = useCallback(async () => {
    setCamError("");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCamError("Ce navigateur ne permet pas la caméra. Saisis le code à la main.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, audio: false,
      });
      streamRef.current = stream;
      setScanOn(true);
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute("playsinline", "true");
      await video.play();

      // Détecteur natif si dispo (Android/ChromeOS), sinon jsQR (partout).
      const nativeDetector = "BarcodeDetector" in window
        // eslint-disable-next-line no-undef
        ? new window.BarcodeDetector({ formats: ["qr_code"] })
        : null;

      const tick = async () => {
        if (!streamRef.current || !video.videoWidth) { rafRef.current = requestAnimationFrame(tick); return; }
        let raw = null;
        try {
          if (nativeDetector) {
            const codes = await nativeDetector.detect(video);
            if (codes && codes.length) raw = codes[0].rawValue;
          } else {
            const c = canvasRef.current;
            c.width = video.videoWidth; c.height = video.videoHeight;
            const ctx = c.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(video, 0, 0, c.width, c.height);
            const img = ctx.getImageData(0, 0, c.width, c.height);
            const found = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
            if (found) raw = found.data;
          }
        } catch (e) { /* frame non prête */ }
        if (raw) { stopScan(); lookup(extractCode(raw), true); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      const name = e && e.name;
      const msg =
        name === "NotAllowedError" ? "Accès caméra refusé — autorise la caméra dans le navigateur."
        : name === "NotFoundError" ? "Aucune caméra détectée sur cet appareil."
        : name === "NotReadableError" ? "La caméra est déjà utilisée par une autre application."
        : "Caméra indisponible (nécessite HTTPS ou localhost).";
      setCamError(msg);
      setScanOn(false);
    }
  }, [lookup]);

  // La caméra s'ouvre en mode scan, s'arrête en mode recherche client.
  useEffect(() => {
    if (authed && mode === "scan") startScan(); else stopScan();
    return () => stopScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, mode]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-yum-pattern flex items-center justify-center px-5 py-10">
        <div className="bg-white text-ink rounded-[28px] p-7 w-full max-w-xs text-center" style={{ boxShadow: "0 12px 0 #a81a0c" }}>
          <img src="/assets/brand/logo.png" alt="YÜM" className="w-24 mx-auto mb-2 drop-shadow" />
          <span className="yum-sticker bg-sun text-ink text-[13px] inline-flex mb-4" style={{ transform: "rotate(-3deg)", boxShadow: "0 4px 0 rgba(0,0,0,.12)" }}>🍳 Espace caisse</span>
          <h1 className="font-display font-bold text-2xl leading-tight">Bonjour l'équipe&nbsp;!</h1>
          <p className="text-ink/60 text-sm mb-5">Entre ton code pour scanner les bons</p>
          <input
            data-testid="caisse-pin" type="password" inputMode="numeric" value={pin}
            onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="• • • •"
            className="w-full bg-cream border-2 border-transparent rounded-2xl px-4 py-3 mb-4 text-ink text-center text-lg tracking-[0.4em] font-bold outline-none focus:border-brand placeholder:tracking-normal placeholder:text-ink/30 placeholder:font-normal"
          />
          <button data-testid="caisse-login" onClick={login} className="yum-cta yum-cta--red w-full">Entrer 🔓</button>
          <p className="mt-4 text-[11px] text-ink/40 font-display">Accès réservé — séparé du back-office</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#14161a] text-white px-4 py-5 flex flex-col items-center">
      <canvas ref={canvasRef} className="hidden" />
      <div className="w-full max-w-sm flex items-center justify-between mb-3">
        <h1 className="font-black text-[#F9B81E] text-lg">🍳 Caisse YÜM</h1>
        <div className="flex items-center gap-2">
          <InstallButton kind="caisse" className="text-[11px] font-bold text-[#1a1206] bg-[#F9B81E] px-3 py-1.5 rounded-full" label="📲 Installer" />
          <button onClick={logout} className="flex items-center gap-1 text-xs text-[#9aa3b2] bg-white/10 px-3 py-1.5 rounded-full"><LogOut size={13} /> Quitter</button>
        </div>
      </div>

      {/* Bascule Scanner / Rechercher un client */}
      <div className="w-full max-w-sm flex gap-2 bg-[#1b1f26] p-1 rounded-2xl mb-4">
        {[["scan", "📷 Scanner un bon"], ["client", "🔍 Rechercher un client"]].map(([k, l]) => (
          <button key={k} onClick={() => { setMode(k); setVoucher(null); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-black ${mode === k ? "bg-[#F9B81E] text-[#1a1206]" : "text-[#9aa3b2]"}`}>{l}</button>
        ))}
      </div>

      {mode === "client" && (
        <div className="w-full max-w-sm">
          <p className="text-sm text-[#9aa3b2] mb-2">Retrouve tous les bons d'un client par son numéro (utilisé ou pas).</p>
          <div className="flex gap-2">
            <input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchClient()}
              inputMode="tel" placeholder="Numéro de téléphone (ex. 0696…)"
              className="flex-1 bg-[#14171b] border border-[#2c323b] rounded-xl px-3 py-3 outline-none focus:border-[#F9B81E] text-sm" />
            <button onClick={searchClient} className="bg-[#F9B81E] text-[#1a1206] px-5 rounded-xl font-black">OK</button>
          </div>

          {clientBusy && <Loader2 className="animate-spin mx-auto mt-6 text-[#F9B81E]" size={26} />}

          {clientData && !clientBusy && (
            clientData.error ? (
              <div className="mt-4 flex items-center gap-2 text-[#ffa0a0] bg-[#1d2127] rounded-2xl p-4"><XCircle size={20} /> {clientData.error}</div>
            ) : (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-black">{clientData.name || clientData.phone}</div>
                  <span className="text-xs text-[#9aa3b2]">{clientData.bons.length} bon{clientData.bons.length > 1 ? "s" : ""}</span>
                </div>
                {clientData.used_today && (
                  <p className="text-[#f2b202] text-xs font-bold bg-[#3a2e10] rounded-xl px-3 py-2 mb-2">⚠️ A déjà utilisé un bon aujourd'hui.</p>
                )}
                {clientData.bons.length === 0 ? (
                  <p className="text-[#7d8590] text-sm text-center py-6">Aucun bon pour ce numéro.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {clientData.bons.map((b) => {
                      const st = BON_ST[b.status] || BON_ST.valid;
                      return (
                        <div key={b.code} className="bg-[#1d2127] border border-[#2c323b] rounded-2xl p-3">
                          <div className="flex items-center justify-between gap-2">
                            <b className="text-white text-sm truncate">{b.label}</b>
                            <span className={`text-[10px] font-black rounded-full px-2 py-1 flex-none ${st.cls}`}>{st.txt}</span>
                          </div>
                          <p className="text-[11px] text-[#7d8590] mt-1 font-mono">{b.code} · {GAME_LABELS[b.game] || b.game}</p>
                          <p className="text-[11px] text-[#9aa3b2]">
                            {b.status === "redeemed" && b.redeemed_at ? <>Utilisé le <b>{fmtD(b.redeemed_at)}</b></>
                              : b.status === "valid" ? <>Valable jusqu'au <b>{fmtD(b.expires_at)}</b></>
                              : <>Gagné le {fmtD(b.claimed_at)}</>}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}

      <div className={`w-full max-w-sm ${mode === "scan" ? "" : "hidden"}`}>
        <div className="rounded-2xl border border-[#2c323b] bg-[#191d23] overflow-hidden aspect-square grid place-items-center relative">
          <video ref={videoRef} className={`w-full h-full object-cover ${scanOn ? "" : "hidden"}`} muted playsInline />
          {scanOn && (
            <div className="pointer-events-none absolute inset-0 grid place-items-center">
              <div className="w-40 h-40 border-2 border-dashed border-[#F9B81E] rounded-2xl" />
              <p className="absolute bottom-3 text-[11px] text-white/90 bg-black/40 px-2 py-1 rounded-full">Vise le QR code du client</p>
            </div>
          )}
          {!scanOn && (
            <div className="text-center px-6">
              <CameraOff size={40} className="text-[#7d8590] mx-auto mb-2" />
              <p className="text-xs text-[#9aa3b2]">{camError || "Caméra arrêtée"}</p>
            </div>
          )}
        </div>

        <button onClick={scanOn ? stopScan : startScan} data-testid="caisse-scan"
          className="w-full mt-3 bg-white/10 hover:bg-white/20 font-bold py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
          {scanOn ? (<><CameraOff size={16} /> Arrêter la caméra</>) : (<><Camera size={16} /> Ouvrir la caméra</>)}
        </button>

        <div className="flex items-center gap-2 my-3 text-[11px] text-[#6b7480]">
          <div className="flex-1 h-px bg-[#2c323b]" /> ou saisis le code <div className="flex-1 h-px bg-[#2c323b]" />
        </div>

        <div className="flex gap-2">
          <input
            data-testid="caisse-manual" value={manual}
            onChange={(e) => setManual(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && lookup(extractCode(manual))}
            placeholder="YUM-XXXXX"
            className="flex-1 bg-[#14171b] border border-[#2c323b] rounded-xl px-3 py-2.5 outline-none focus:border-[#F9B81E] uppercase tracking-wide text-sm"
          />
          <button onClick={() => lookup(extractCode(manual))} className="bg-[#F9B81E] text-[#1a1206] px-5 rounded-xl font-black text-sm">OK</button>
        </div>

        {busy && <Loader2 className="animate-spin mx-auto mt-5 text-[#F9B81E]" size={26} />}

        {voucher && !busy && voucher.justRedeemed && (
          <div className="mt-4 rounded-2xl border-2 border-[#3ecf6f] bg-[#12301d] p-6 text-center yum-pop" data-testid="caisse-confirm">
            <div className="text-5xl mb-2">✅</div>
            <p className="text-[#3ecf6f] font-black text-2xl">BON VALIDÉ</p>
            <p className="text-white font-bold mt-1 text-lg">{voucher.label}</p>
            <p className="text-[#9aa3b2] text-sm mt-1 font-mono">{voucher.first_name || "—"} · {voucher.code}</p>
            <p className="text-[#7d8590] text-xs mt-3">Bon marqué comme utilisé ✓ — un seul bon par jour et par client.</p>
          </div>
        )}

        {voucher && !busy && !voucher.justRedeemed && (
          <div className="mt-4 rounded-2xl border border-[#2c323b] bg-[#1d2127] p-4" data-testid="caisse-result">
            {voucher.error ? (
              <div className="flex items-center gap-2 text-[#ffa0a0]"><XCircle size={20} /> {voucher.error}</div>
            ) : voucher.error2 ? (
              <div className="flex items-center gap-2 text-[#ffa0a0]"><XCircle size={20} /> {voucher.error2}</div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <b className="text-white">{voucher.label}</b>
                  <span className={`text-[10px] font-black rounded-full px-2 py-1 ${
                    voucher.blocked_today ? "bg-[#3a2e10] text-[#f2b202]"
                    : voucher.status === "valid" ? "bg-[#12301d] text-[#3ecf6f]"
                    : voucher.status === "expired" ? "bg-[#3a1414] text-[#ff8a8a]"
                    : "bg-[#2a2e35] text-[#8b94a0]"}`}>
                    {voucher.blocked_today ? "1 BON/JOUR"
                      : voucher.status === "valid" ? "VALIDE"
                      : voucher.status === "expired" ? "EXPIRÉ" : "DÉJÀ UTILISÉ"}
                  </span>
                </div>
                <p className="text-[11px] text-[#9aa3b2] mt-1 font-mono">
                  {voucher.code} · {voucher.first_name || "—"} · {GAME_LABELS[voucher.game] || voucher.game}
                </p>
                {voucher.expires_at && (
                  <p className="text-[11px] text-[#9aa3b2] mt-0.5">Valable jusqu'au {new Date(voucher.expires_at).toLocaleDateString("fr-FR")}</p>
                )}
                {voucher.blocked_today ? (
                  <p className="text-center text-[#f2b202] text-sm mt-3 font-bold">⚠️ Ce client a déjà utilisé un bon aujourd'hui.<br /><span className="font-normal text-[#9aa3b2]">Un seul bon par jour et par personne.</span></p>
                ) : voucher.status === "valid" ? (
                  <button onClick={redeem} data-testid="caisse-redeem" className="w-full mt-3 bg-[#3ecf6f] text-[#06230f] font-black py-2.5 rounded-xl flex items-center justify-center gap-2">
                    <CheckCircle2 size={18} /> Marquer comme utilisé
                  </button>
                ) : (
                  <p className="text-center text-[#8b94a0] text-sm mt-3">
                    {voucher.status === "expired" ? "Ce bon a expiré." : "Ce bon a déjà été validé."}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <p className="text-[10px] text-[#6b7480] font-mono mt-6">Équipe magasin — accès séparé du back-office</p>
    </div>
  );
}
